// Billing → "Missing a bill" (Stephanie 2026-10-20): every PO the supplier
// has shipped or delivered with no bill entered yet, oldest first. Each row
// opens "Enter a bill" on that PO (the project's Bills tab), starting from
// the PO's lines.
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { TABLE_HEAD, TD, TH, TROW } from "@/components/ui/RecordParts";
import { cn } from "@/lib/utils";

export interface MissingBillRow {
  poId: string;
  poNumber: number;
  stage: "shipped" | "delivered";
  vendorName: string;
  poCost: string | null;
  orderNumber: number | null;
  projectId: string | null;
  projectName: string | null;
  clientName: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  daysWaiting: number | null;
}

export function useMissingBills(enabled = true) {
  return useQuery({
    queryKey: ["finance", "bills", "missing"],
    queryFn: () => api<{ rows: MissingBillRow[] }>("/bills/missing"),
    enabled,
  });
}

const money = (v: string | null) => (v === null ? "—" : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" }));
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

export function MissingBillsTable() {
  const navigate = useNavigate();
  const q = useMissingBills();
  const rows = q.data?.rows ?? [];
  if (q.isLoading) {
    return (
      <div className="space-y-3 p-5" aria-label="Loading">
        {[0, 1, 2].map((i) => <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />)}
      </div>
    );
  }
  if (q.isError) return <div className="p-5 text-sm text-risk" role="alert">We couldn't load this list. Refresh to try again.</div>;
  if (rows.length === 0) {
    return (
      <div className="px-5 py-12 text-center">
        <p className="font-medium text-ink">Every shipped PO has a bill.</p>
        <p className="mt-1 text-sm text-muted">A PO shows here once the supplier ships it, until its bill is entered.</p>
      </div>
    );
  }
  return (
    <table className="w-full text-left">
      <thead className={TABLE_HEAD}>
        <tr>
          <th scope="col" className={TH}>Vendor / PO</th>
          <th scope="col" className={`${TH} hidden md:table-cell`}>Client / project</th>
          <th scope="col" className={TH}>Shipped</th>
          <th scope="col" className={`${TH} text-right`}>Waiting</th>
          <th scope="col" className={`${TH} hidden sm:table-cell text-right`}>PO cost</th>
        </tr>
      </thead>
      <tbody className="text-[13px]">
        {rows.map((r) => {
          const enter = r.projectId ? `/projects/${r.projectId}/bills?bill_po=${r.poId}` : `/pos/${r.poId}`;
          return (
          <tr key={r.poId} onClick={() => navigate(enter)} className={cn(TROW, "cursor-pointer")}>
            <td className={TD}>
              <Link to={enter} onClick={(e) => e.stopPropagation()} className="font-medium text-ink hover:underline">
                {r.vendorName}
              </Link>
              <span className="block text-[11px] text-muted">
                <Link to={`/pos/${r.poId}`} onClick={(e) => e.stopPropagation()} className="hover:underline">PO #{r.poNumber}</Link>
                {r.orderNumber ? ` · order #${r.orderNumber}` : ""}
              </span>
            </td>
            <td className={`${TD} hidden md:table-cell`}>
              <span className="text-ink">{r.clientName ?? "—"}</span>
              <span className="block text-[11px] text-muted">{r.projectName ?? ""}</span>
            </td>
            <td className={TD}>
              {r.stage === "delivered" ? `Delivered ${day(r.deliveredAt)}` : `Shipped ${day(r.shippedAt)}`}
            </td>
            <td className={cn(TD, "text-right tabular-nums", (r.daysWaiting ?? 0) >= 14 ? "font-semibold text-warn" : "text-ink")}>
              {r.daysWaiting === null ? "—" : `${r.daysWaiting} ${r.daysWaiting === 1 ? "day" : "days"}`}
            </td>
            <td className={`${TD} hidden sm:table-cell text-right tabular-nums text-ink`}>{money(r.poCost)}</td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}
